import React, { useState } from 'react';
import { AlertCircle, Eye, EyeOff, KeyRound, Lock } from 'lucide-react';
import BrandLogo from '../components/ui/BrandLogo';
import { useAuth } from '../context/AuthContext';

const ForcePasswordChange = () => {
  const { changePassword, currentUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Completa todos los campos.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('La confirmación no coincide con la nueva contraseña.');
      return;
    }

    if (newPassword.length < 8) {
      setError('La nueva contraseña debe tener al menos 8 caracteres.');
      return;
    }

    setLoading(true);

    try {
      await changePassword({
        email: currentUser?.email,
        currentPassword,
        newPassword,
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'No fue posible actualizar la contraseña.');
    } finally {
      setLoading(false);
    }
  };

  const renderPasswordField = ({
    id,
    label,
    value,
    setValue,
    visible,
    setVisible,
    placeholder,
  }) => (
    <div>
      <label htmlFor={id} className="mb-2 block text-sm font-medium text-slate-200">
        {label}
      </label>
      <div className="relative">
        <Lock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          required
          className="w-full rounded-xl border border-white/20 bg-white/10 py-3 pl-11 pr-12 text-white placeholder-slate-400 transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={() => setVisible((prev) => !prev)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-white"
          aria-label={visible ? `Ocultar ${label.toLowerCase()}` : `Mostrar ${label.toLowerCase()}`}
        >
          {visible ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950">
      <div className="absolute left-0 top-0 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
      <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />

      <div className="relative z-10 mx-4 w-full max-w-lg">
        <div className="mb-8 text-center">
          <div className="mb-4 flex justify-center">
            <BrandLogo variant="dark" className="h-32 opacity-60 saturate-90 drop-shadow-xl" />
          </div>
          <p className="text-slate-300">Debes cambiar tu contraseña antes de continuar.</p>
        </div>

        <div className="rounded-2xl border border-white/20 bg-white/10 p-8 shadow-2xl backdrop-blur-xl">
          <div className="mb-6 rounded-2xl border border-amber-400/40 bg-amber-500/10 p-4 text-amber-100">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-300" />
              <div>
                <p className="font-semibold">Cambio obligatorio de contraseña</p>
                <p className="mt-1 text-sm text-amber-100/90">
                  Este usuario tiene una contraseña temporal. Define una nueva contraseña personal para ingresar al sistema.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {renderPasswordField({
              id: 'current-password',
              label: 'Contraseña temporal',
              value: currentPassword,
              setValue: setCurrentPassword,
              visible: showCurrentPassword,
              setVisible: setShowCurrentPassword,
              placeholder: 'Ingresa la contraseña temporal',
            })}

            {renderPasswordField({
              id: 'new-password',
              label: 'Nueva contraseña',
              value: newPassword,
              setValue: setNewPassword,
              visible: showNewPassword,
              setVisible: setShowNewPassword,
              placeholder: 'Crea una contraseña nueva',
            })}

            {renderPasswordField({
              id: 'confirm-password',
              label: 'Confirmar nueva contraseña',
              value: confirmPassword,
              setValue: setConfirmPassword,
              visible: showConfirmPassword,
              setVisible: setShowConfirmPassword,
              placeholder: 'Repite la nueva contraseña',
            })}

            {error && (
              <div className="flex items-center gap-2 rounded-xl border border-red-500/50 bg-red-500/20 p-3 text-sm text-red-100">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 font-medium text-white shadow-lg transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  <span>Actualizando contraseña...</span>
                </>
              ) : (
                <>
                  <KeyRound className="h-5 w-5" />
                  <span>Guardar nueva contraseña</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForcePasswordChange;
